import { useEffect, useState } from "react";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";

const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "webp", "gif", "jfif"]);

// The stored link keeps the original filename, and phones often save scans as
// `.PDF`. A case-sensitive search for "pdf" then skips the file and shows the
// empty preview instead of the document.
const extensionOf = (url) => {
  const value = String(url || "");
  try {
    const path = decodeURIComponent(new URL(value).pathname);
    const base = path.split("/").pop() || "";
    const match = base.match(/\.([a-z0-9]+)$/i);
    if (match) return match[1].toLowerCase();
  } catch {
    // Not a full URL. Fall through and read whatever suffix is present.
  }
  const match = value.split("?")[0].match(/\.([a-z0-9]+)$/i);
  return match ? match[1].toLowerCase() : "";
};

const isPdfBytes = (bytes) =>
  bytes.length >= 4 &&
  bytes[0] === 0x25 &&
  bytes[1] === 0x50 &&
  bytes[2] === 0x44 &&
  bytes[3] === 0x46;

function IdProofPreview({ url }) {
  const [preview, setPreview] = useState({ kind: "loading" });

  useEffect(() => {
    if (!url) {
      setPreview({ kind: "missing" });
      return undefined;
    }

    const extension = extensionOf(url);
    if (IMAGE_EXTENSIONS.has(extension)) {
      setPreview({ kind: "image", src: url });
      return undefined;
    }

    let cancelled = false;
    const owned = [];

    const load = async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error("Could not read the file");
        const type = response.headers.get("content-type") || "";
        const buffer = await response.arrayBuffer();
        const bytes = new Uint8Array(buffer.slice(0, 5));
        const pdf = type.includes("pdf") || extension === "pdf" || isPdfBytes(bytes);
        if (cancelled) return;

        if (pdf) {
          const src = URL.createObjectURL(new Blob([buffer], { type: "application/pdf" }));
          owned.push(src);
          setPreview({ kind: "pdf", src });
          return;
        }

        if (type.startsWith("image/")) {
          const src = URL.createObjectURL(new Blob([buffer], { type }));
          owned.push(src);
          setPreview({ kind: "image", src });
          return;
        }

        setPreview({ kind: "unsupported" });
      } catch {
        if (cancelled) return;
        // Opening the link directly still works when the browser blocks the read.
        if (extension === "pdf") setPreview({ kind: "pdf", src: url });
        else setPreview({ kind: "unsupported" });
      }
    };

    load();

    return () => {
      cancelled = true;
      owned.forEach((src) => URL.revokeObjectURL(src));
    };
  }, [url]);

  if (preview.kind === "pdf") {
    return (
      <iframe
        title="ID proof"
        src={preview.src}
        style={{ width: "100%", height: "100%", border: 0 }}
      />
    );
  }

  if (preview.kind === "image") {
    return (
      <img
        src={preview.src}
        alt="ID proof"
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
      />
    );
  }

  if (preview.kind === "loading") {
    return (
      <Typography sx={{ color: "gray", textAlign: "center", padding: 2 }}>
        Opening the file…
      </Typography>
    );
  }

  return (
    <Typography sx={{ color: "gray", textAlign: "center", padding: 2 }}>
      Cannot preview this file type.{" "}
      {url ? (
        <Link href={url} target="_blank" rel="noopener noreferrer">
          Download to view.
        </Link>
      ) : (
        "Download to view."
      )}
    </Typography>
  );
}

IdProofPreview.propTypes = {
  url: PropTypes.string,
};

IdProofPreview.defaultProps = {
  url: "",
};

export default IdProofPreview;
